import React, { useContext, useEffect, useMemo } from 'react';

import { LT_PUSHER } from 'bl/loggers.bl';
import PUSHER from 'config/pusher.singleton';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import logger from 'utils/logger.util';
import isNilValue from 'utils/predicates/any/isNilValue.util';
import isEmptyArray from 'utils/predicates/array/isEmptyArray.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import { isNotValidId } from 'utils/predicates/fields.util';
import isFn from 'utils/predicates/function/isFn.util';
import isNotFn from 'utils/predicates/function/isNotFn.util';

const L = logger(['PusherProvider', LT_PUSHER]); // eslint-disable-line no-unused-vars

const USE_LOG = true;

export const PUSHER_EVENT_BOOKING_TYPE = 'booking';
export const PUSHER_EVENT_CHECKOUT_SCAN = 'scan';
export const PUSHER_EVENT_CLIENT = 'client';
export const PUSHER_EVENT_DEPOSIT_STATUS_CHANGE = 'dr_status_changed';
export const PUSHER_EVENT_INFO = 'info';
export const PUSHER_EVENT_MISSED_CALL = 'missedcall';
export const PUSHER_EVENT_ONLINE_VOUCHER_SALE = 'onlinevouchersale';
export const PUSHER_EVENT_SMS_CREDITS_AVAILABLE = 'sms_credits_available';
export const PUSHER_EVENT_SMS_THREAD = 'smsthread';
export const PUSHER_EVENT_CLEAR_AP_CACHE = 'clear_appointment_cache';
export const PUSHER_EVENT_SALON_REFRESH = 'salon_data_changed_refresh';

L.debug('Available events:', [
  PUSHER_EVENT_BOOKING_TYPE,
  PUSHER_EVENT_SMS_THREAD,
  PUSHER_EVENT_INFO,
  PUSHER_EVENT_MISSED_CALL,
  PUSHER_EVENT_CLIENT,
  PUSHER_EVENT_INFO,
  PUSHER_EVENT_ONLINE_VOUCHER_SALE,
  PUSHER_EVENT_DEPOSIT_STATUS_CHANGE,
]).sort();

const Context = React.createContext(void 1);

export const usePusherSalonEvent = (eventName) => {
  const salonId = useCurrentSalonId();
  const [result, setResult] = useStateWhileMounted(null);

  useEffect(
    () =>
      isNotValidId(salonId)
        ? void 1
        : PUSHER.single({
            salonId,
            eventName,
            log: USE_LOG,
            fn: (ev) => void setResult(ev),
          }),
    [eventName, salonId, setResult],
  );

  return result;
};

export const PusherProvider = ({ children }) => {
  const apCacheStale = usePusherSalonEvent(PUSHER_EVENT_CLEAR_AP_CACHE);
  const drStatusChanged = usePusherSalonEvent(PUSHER_EVENT_DEPOSIT_STATUS_CHANGE);
  const info = usePusherSalonEvent(PUSHER_EVENT_INFO);
  const salonRefresh = usePusherSalonEvent(PUSHER_EVENT_SALON_REFRESH);

  const value = useMemo(
    () => ({ apCacheStale, drStatusChanged, info, salonRefresh }),
    [apCacheStale, drStatusChanged, info, salonRefresh],
  );

  L.debug('()', 'value:', value);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useStaleAppointmentIds = () => {
  const value = useContext(Context);

  const appointmentIds = value?.apCacheStale?.data?.results?.appointment_ids;

  return useMemo(() => {
    if (isFullArray(appointmentIds)) return appointmentIds;

    if (!isNilValue(appointmentIds) && !isEmptyArray(appointmentIds)) {
      L.warn('useStaleAppointmentIds()', 'Bad appointmentIds:', appointmentIds);
    }

    return asArray();
  }, [appointmentIds]);
};

export const useSalonNeedsReload = () => {
  const value = useContext(Context);

  const valueRefreshPage = value?.salonRefresh?.data?.results?.refresh_page;

  return useMemo(() => {
    const boolean = fromAnyToBool(valueRefreshPage);
    L.debug('useSalonNeedsReload()', boolean, '<-', valueRefreshPage);
    return boolean;
  }, [valueRefreshPage]);
};

export const usePusherProviderData = (selector) => {
  const value = useContext(Context);

  const memoizedSelectedData = useMemo(() => {
    if (isFn(selector)) return selector(value);
  }, [selector, value]);

  if (isNotFn(selector)) L.error('usePusherData()', 'Invalid selector:', selector);

  return memoizedSelectedData;
};
